import React, { useEffect, useMemo, useState } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { fmtCLP } from "../lib/format";
import { toast } from "sonner";
import { Plus, ChevronRight, ChevronDown, Trash2, Lock } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const TIPO_LABELS = {
  activo: "Activo",
  pasivo: "Pasivo",
  patrimonio: "Patrimonio",
  ingreso: "Ingreso",
  egreso: "Egreso",
};

const TIPO_COLORS = {
  activo: "bg-primary/10 text-primary",
  pasivo: "bg-destructive/10 text-destructive",
  patrimonio: "bg-warning/20 text-warning-foreground",
  ingreso: "bg-success/10 text-success",
  egreso: "bg-muted-foreground/10 text-muted-foreground",
};

const esDeudora = (tipo) => tipo === "activo" || tipo === "egreso";

const saldoPropio = (c) => {
  const v = c.saldo != null ? c.saldo : c.saldo_inicial;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Padre por código cuando no hay parent_id: "1.1.01" cuelga de "1.1" (el prefijo más largo existente).
const padrePorCodigo = (c, porCodigo) => {
  const partes = String(c.codigo || "").split(".");
  for (let i = partes.length - 1; i > 0; i--) {
    const p = porCodigo.get(partes.slice(0, i).join("."));
    if (p && p.id !== c.id) return p;
  }
  return null;
};

export default function PlanCuentas() {
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState({
    codigo: "",
    nombre: "",
    tipo: "activo",
    parent_id: "",
    imputable: true,
  });

  const load = async () => {
    try {
      const { data } = await api.get("/cuentas", { params: { con_saldos: true } });
      setCuentas(data);
      // Expand top level by default
      setExpanded(new Set(data.filter((c) => !c.parent_id).map((c) => c.id)));
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const { hijosPorPadre, saldoMostrado, conSaldos } = useMemo(() => {
    const porId = new Map(cuentas.map((c) => [c.id, c]));
    const porCodigo = new Map(cuentas.map((c) => [c.codigo, c]));
    const hijos = new Map();
    for (const c of cuentas) {
      let padre = null;
      if (c.parent_id) padre = porId.get(c.parent_id) || null;
      else padre = padrePorCodigo(c, porCodigo);
      const key = padre ? padre.id : null;
      if (!hijos.has(key)) hijos.set(key, []);
      hijos.get(key).push(c);
    }
    for (const lista of hijos.values()) lista.sort((a, b) => String(a.codigo).localeCompare(String(b.codigo)));

    // Acumulado en signo deudor (débito positivo) para sumar subcuentas de distinta naturaleza.
    const deudorAcum = new Map();
    const acumular = (c, visitando) => {
      if (deudorAcum.has(c.id)) return deudorAcum.get(c.id);
      if (visitando.has(c.id)) return 0;
      visitando.add(c.id);
      let total = esDeudora(c.tipo) ? saldoPropio(c) : -saldoPropio(c);
      for (const h of hijos.get(c.id) || []) total += acumular(h, visitando);
      visitando.delete(c.id);
      deudorAcum.set(c.id, total);
      return total;
    };

    const mostrado = new Map();
    for (const c of cuentas) {
      const agrupa = !c.imputable || (hijos.get(c.id) || []).length > 0;
      if (agrupa) {
        const d = acumular(c, new Set());
        mostrado.set(c.id, { valor: esDeudora(c.tipo) ? d : -d, acumulado: true });
      } else {
        mostrado.set(c.id, { valor: saldoPropio(c), acumulado: false });
      }
    }

    return {
      hijosPorPadre: hijos,
      saldoMostrado: mostrado,
      conSaldos: cuentas.some((c) => c.saldo != null),
    };
  }, [cuentas]);

  const childrenOf = (parentId) => hijosPorPadre.get(parentId) || [];

  const toggle = (id) => {
    const s = new Set(expanded);
    s.has(id) ? s.delete(id) : s.add(id);
    setExpanded(s);
  };

  const renderRow = (c, depth = 0) => {
    const hijos = childrenOf(c.id);
    const isOpen = expanded.has(c.id);
    const saldo = saldoMostrado.get(c.id) || { valor: saldoPropio(c), acumulado: false };
    const conSubcuentas = Boolean(c.tiene_subcuentas) || hijos.length > 0;
    const conMovimientos = Boolean(c.tiene_movimientos);
    const motivoBloqueo =
      conSubcuentas && conMovimientos
        ? "Tiene subcuentas y movimientos"
        : conSubcuentas
          ? "Tiene subcuentas"
          : conMovimientos
            ? "Tiene movimientos"
            : null;
    return (
      <React.Fragment key={c.id}>
        <tr className="hover:bg-muted" data-testid={`cuenta-row-${c.codigo}`}>
          <td>
            <div className="flex items-center gap-1" style={{ paddingLeft: depth * 18 }}>
              {hijos.length > 0 ? (
                <button onClick={() => toggle(c.id)} className="text-muted-foreground hover:text-primary">
                  {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                </button>
              ) : (
                <span className="w-3.5" />
              )}
              <span className="num text-foreground">{c.codigo}</span>
            </div>
          </td>
          <td>
            <span className={depth === 0 ? "font-semibold text-foreground" : "text-foreground"}>{c.nombre}</span>
          </td>
          <td>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] tracking-[0.1em] uppercase font-semibold ${TIPO_COLORS[c.tipo]}`}>
              {TIPO_LABELS[c.tipo]}
            </span>
          </td>
          <td>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] text-muted-foreground">
                {esDeudora(c.tipo) ? "Deudora" : "Acreedora"}
              </span>
              <span
                className={`inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[10px] leading-none ${
                  c.imputable ? "border-primary/30 text-primary" : "border-border text-muted-foreground"
                }`}
                title={c.imputable ? "Acepta movimientos" : "Agrupa subcuentas; no acepta movimientos"}
              >
                {c.imputable ? "Imputable" : "Agrupadora"}
              </span>
            </div>
          </td>
          <td
            className={`num whitespace-nowrap ${saldo.valor < 0 ? "text-destructive font-medium" : ""} ${
              saldo.acumulado ? "font-semibold" : ""
            }`}
            title={saldo.acumulado ? "Incluye el saldo de sus subcuentas" : undefined}
            data-testid={`saldo-cuenta-${c.codigo}`}
          >
            {fmtCLP(saldo.valor)}
          </td>
          <td>
            {motivoBloqueo ? (
              <span
                className="inline-flex p-1 text-muted-foreground/60 cursor-not-allowed"
                title={`No se puede eliminar: ${motivoBloqueo}`}
                aria-label={`No se puede eliminar: ${motivoBloqueo}`}
                data-testid={`delete-cuenta-locked-${c.codigo}`}
              >
                <Lock className="h-3.5 w-3.5" />
              </span>
            ) : (
              <button
                onClick={() => handleDelete(c)}
                data-testid={`delete-cuenta-${c.codigo}`}
                className="text-destructive hover:bg-destructive/10 p-1 rounded-sm"
                title="Eliminar"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </td>
        </tr>
        {isOpen && hijos.map((h) => renderRow(h, depth + 1))}
      </React.Fragment>
    );
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`¿Eliminar cuenta ${c.codigo} - ${c.nombre}?`)) return;
    try {
      await api.delete(`/cuentas/${c.id}`);
      toast.success("Cuenta eliminada");
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/cuentas", {
        ...form,
        parent_id: form.parent_id || null,
        saldo_inicial: 0,
      });
      toast.success("Cuenta creada");
      setOpenDialog(false);
      setForm({ codigo: "", nombre: "", tipo: "activo", parent_id: "", imputable: true });
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const roots = childrenOf(null);

  return (
    <Page>
      <PageHeader
        title="Plan de cuentas"
        subtitle="Árbol contable jerárquico"
        testid="plan-cuentas-header"
        actions={
          <Dialog open={openDialog} onOpenChange={setOpenDialog}>
            <DialogTrigger asChild>
              <button
                data-testid="new-cuenta-button"
                className="inline-flex items-center gap-1.5 bg-primary hover:bg-success text-primary-foreground text-sm font-medium px-3 py-2 rounded-sm transition-colors"
              >
                <Plus className="h-4 w-4" /> Nueva cuenta
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nueva cuenta contable</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-3" data-testid="new-cuenta-form">
                <div>
                  <label className="label-caps block mb-1">Código</label>
                  <input
                    required
                    data-testid="cuenta-codigo"
                    value={form.codigo}
                    onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                    placeholder="ej: 1.1.05"
                    className="w-full px-3 py-2 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="label-caps block mb-1">Nombre</label>
                  <input
                    required
                    data-testid="cuenta-nombre"
                    value={form.nombre}
                    onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm"
                  />
                </div>
                <div>
                  <label className="label-caps block mb-1">Tipo</label>
                  <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                    <SelectTrigger data-testid="cuenta-tipo" className="text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(TIPO_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="label-caps block mb-1">Cuenta padre (opcional)</label>
                  <Select value={form.parent_id || "none"} onValueChange={(v) => setForm({ ...form, parent_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="cuenta-parent" className="text-sm">
                      <SelectValue placeholder="Ninguna" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Ninguna —</SelectItem>
                      {cuentas
                        .filter((c) => !c.imputable && c.tipo === form.tipo)
                        .map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.codigo} · {c.nombre}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="imputable"
                    checked={form.imputable}
                    onChange={(e) => setForm({ ...form, imputable: e.target.checked })}
                    data-testid="cuenta-imputable"
                  />
                  <label htmlFor="imputable" className="text-sm text-foreground">
                    Imputable (acepta movimientos)
                  </label>
                </div>
                <DialogFooter>
                  <button
                    type="submit"
                    data-testid="submit-cuenta"
                    className="bg-primary hover:bg-success text-primary-foreground text-sm font-medium px-4 py-2 rounded-sm"
                  >
                    Crear cuenta
                  </button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-card border border-border rounded-md overflow-x-auto" data-testid="cuentas-table-container">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground text-sm">Cargando…</div>
        ) : cuentas.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">No hay cuentas. Crea la primera.</div>
        ) : (
          <table className="acc-table" data-testid="cuentas-table">
            <thead>
              <tr>
                <th style={{ width: 160 }}>Código</th>
                <th>Nombre</th>
                <th style={{ width: 110 }}>Tipo</th>
                <th style={{ width: 150 }}>Naturaleza</th>
                <th className="num" style={{ width: 140 }}>{conSaldos ? "Saldo" : "Saldo inicial"}</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>{roots.map((r) => renderRow(r, 0))}</tbody>
          </table>
        )}
      </div>
    </Page>
  );
}
